/**
 * UrgentTabTitle — Met à jour `document.title` avec un compteur de
 * rappels AVI urgents (< 1h) pour que l'utilisateur voie l'urgence
 * même quand l'onglet du navigateur n'est pas actif ou quand
 * plusieurs onglets sont ouverts.
 *
 * Spec iter201 (2026-02-28) — poll léger toutes les 60s + refresh au
 * refocus onglet. Titre restauré à « Le Desk » dès que le compteur
 * retombe à 0.
 * Spec iter202 (2026-02-28) — ajoute aussi un point rouge sur le
 * favicon (32x32 canvas overlay). Restaure le favicon original dès
 * que le compteur retombe à 0.
 */

import { useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BASE_TITLE = "Le Desk";
const POLL_MS = 60000;
const FAVICON_URL = "/favicon.png?v=6";

/** Dessine un point rouge en haut à droite d'une image de favicon.
 *  Renvoie une data URL PNG à assigner à `<link rel="icon">`. */
function buildFaviconWithDot(imgSrc) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const size = 64;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, size, size);
      // Point rouge en haut à droite. Ratio 32 % du côté (≈ 20 px sur 64).
      const r = Math.round(size * 0.22);
      const cx = size - r - 2;
      const cy = r + 2;
      // Anneau blanc pour ressortir sur les favicons sombres/clairs.
      ctx.beginPath();
      ctx.arc(cx, cy, r + 2, 0, Math.PI * 2);
      ctx.fillStyle = "#FFFFFF";
      ctx.fill();
      // Cercle rouge principal.
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = "#EF4444";
      ctx.fill();
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = imgSrc;
  });
}

/** Applique un href sur tous les <link rel="icon"> déjà présents. */
function setFaviconHref(href) {
  const links = document.querySelectorAll("link[rel~='icon'], link[rel='shortcut icon']");
  links.forEach((l) => {
    // On préserve l'original la 1ʳᵉ fois pour pouvoir le restaurer.
    if (!l.dataset.origHref) l.dataset.origHref = l.getAttribute("href") || "";
    l.setAttribute("href", href);
  });
}

/** Restaure le href original enregistré au 1er `setFaviconHref`. */
function restoreFavicon() {
  const links = document.querySelectorAll("link[rel~='icon'], link[rel='shortcut icon']");
  links.forEach((l) => {
    if (l.dataset.origHref) l.setAttribute("href", l.dataset.origHref);
  });
}

export default function UrgentTabTitle() {
  const { user } = useAuth();
  const timerRef = useRef(null);
  const cancelRef = useRef(false);
  const lastCountRef = useRef(0);
  const dotFaviconRef = useRef(null); // data URL du favicon avec point

  useEffect(() => {
    if (!user) {
      document.title = BASE_TITLE;
      restoreFavicon();
      cancelRef.current = true;
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    cancelRef.current = false;

    const applyState = async (count) => {
      // Titre
      document.title = count > 0 ? `(${count}) ${BASE_TITLE}` : BASE_TITLE;
      // Favicon
      if (count > 0) {
        try {
          if (!dotFaviconRef.current) {
            dotFaviconRef.current = await buildFaviconWithDot(FAVICON_URL);
          }
          if (!cancelRef.current) setFaviconHref(dotFaviconRef.current);
        } catch {
          // Silencieux : conserve le favicon original si le canvas
          // échoue (rare — image locale même origine).
        }
      } else {
        restoreFavicon();
      }
    };

    const fetchCount = async () => {
      try {
        const { data } = await api.get("/avi/urgent-count");
        if (cancelRef.current) return;
        const n = Number(data?.count) || 0;
        // Optimisation : n'applique que si le compteur a changé.
        if (n !== lastCountRef.current) {
          lastCountRef.current = n;
          await applyState(n);
        }
      } catch {
        // Silencieux.
      }
    };

    fetchCount();
    timerRef.current = setInterval(fetchCount, POLL_MS);
    const onFocus = () => fetchCount();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelRef.current = true;
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener("focus", onFocus);
      document.title = BASE_TITLE;
      restoreFavicon();
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
